import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { ClipboardList, Save, FileText, ChevronDown, ChevronUp, Package } from 'lucide-react';

// Manual-only fields — the rest of the tracking sheet auto-merges from the
// existing CRM data (status_history, invoices, supplier_orders, completion
// certificate) on the backend.
const MANUAL_STEPS = [
  { flag: 'credit_check',    date: 'check_date',        label: 'Credit Check' },
  { flag: 'cust_notified',   date: 'notification_date', label: 'Cust Notified' },
  { flag: 'extra_labour',    date: 'labour_date',       label: 'Extra Labour' },
  { flag: 'extra_materials', date: 'materials_date',    label: 'Extra Materials' },
  { flag: 'follow_up',       date: 'follow_up_date',    label: 'Follow Up' },
];

const READINESS_COLOURS = {
  green: { dot: 'bg-emerald-500', text: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-200', label: 'Ready to start' },
  amber: { dot: 'bg-amber-500',   text: 'text-amber-700',   bg: 'bg-amber-50',   border: 'border-amber-200',   label: 'Partly ready' },
  red:   { dot: 'bg-red-500',     text: 'text-red-700',     bg: 'bg-red-50',     border: 'border-red-200',     label: 'Materials pending' },
  grey:  { dot: 'bg-slate-400',   text: 'text-slate-600',   bg: 'bg-slate-50',   border: 'border-slate-200',   label: 'No materials yet' },
};

export default function JobTrackingSheetCard({ appraisalId, onOpenPdf }) {
  const { api } = useAuth();
  const [open, setOpen] = useState(false);
  const [sheet, setSheet] = useState({});
  const [readiness, setReadiness] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/appraisals/${appraisalId}`);
      setSheet(r.data?.tracking_sheet || {});
    } catch { /* ignore */ }
  }, [api, appraisalId]);

  const loadReadiness = useCallback(async () => {
    try {
      const r = await api.get(`/appraisals/${appraisalId}/materials-readiness`);
      setReadiness(r.data);
    } catch { /* ignore */ }
  }, [api, appraisalId]);

  // Readiness fetched on mount so the badge is always visible without expanding
  useEffect(() => { loadReadiness(); }, [loadReadiness]);
  useEffect(() => { if (open) load(); }, [open, load]);

  const setField = (k, v) => { setSheet(s => ({ ...s, [k]: v })); setSaved(false); };

  const save = async () => {
    setSaving(true); setSaved(false);
    try {
      await api.patch(`/appraisals/${appraisalId}/tracking-sheet`, sheet);
      setSaved(true);
      setTimeout(() => setSaved(false), 2200);
    } catch (e) {
      console.error('Save tracking sheet failed', e);
      alert('Failed to save. Please try again.');
    }
    setSaving(false);
  };

  return (
    <div data-testid="tracking-sheet-card" className="rounded-lg border border-[#E2E2DF] bg-white overflow-hidden">
      <button
        type="button"
        data-testid="tracking-sheet-toggle"
        onClick={() => setOpen(v => !v)}
        className="w-full px-4 py-3 flex items-center justify-between gap-3 hover:bg-slate-50"
      >
        <div className="flex items-center gap-3 text-left">
          <div className="w-9 h-9 rounded-md bg-purple-50 text-[#7E22CE] flex items-center justify-center">
            <ClipboardList size={18} />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-slate-900">Appraisal Tracking Sheet</h3>
            <p className="text-xs text-slate-500">
              Auto-merges acceptance / deposit / start / completed / invoiced / paid from your CRM data. Edit the manual fields below.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {readiness && (
            <span
              data-testid="materials-readiness-badge"
              title={`Materials: ${readiness.arrived}/${readiness.total} arrived (${readiness.percent_arrived}%) · ${readiness.ordered}/${readiness.total} ordered (${readiness.percent_ordered}%)`}
              className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-[10px] font-semibold border ${(READINESS_COLOURS[readiness.status] || READINESS_COLOURS.grey).bg} ${(READINESS_COLOURS[readiness.status] || READINESS_COLOURS.grey).text} ${(READINESS_COLOURS[readiness.status] || READINESS_COLOURS.grey).border}`}
            >
              <span className={`w-2 h-2 rounded-full ${(READINESS_COLOURS[readiness.status] || READINESS_COLOURS.grey).dot}`} />
              <Package size={11} />
              {readiness.total > 0 ? `${readiness.arrived}/${readiness.total}` : '—'}
              <span className="hidden sm:inline">· {(READINESS_COLOURS[readiness.status] || READINESS_COLOURS.grey).label}</span>
            </span>
          )}
          {open ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
        </div>
      </button>

      {open && (
        <div className="border-t border-[#E2E2DF] p-4 bg-slate-50/40 space-y-3">
          {MANUAL_STEPS.map(s => (
            <div key={s.flag} className="grid grid-cols-12 gap-3 items-center">
              <label className="col-span-1 flex items-center justify-center">
                <input
                  type="checkbox"
                  data-testid={`tk-${s.flag}`}
                  checked={!!sheet[s.flag]}
                  onChange={(e) => setField(s.flag, e.target.checked)}
                  className="h-4 w-4 rounded border-slate-300 text-[#7E22CE] focus:ring-[#7E22CE]"
                />
              </label>
              <span className="col-span-4 text-sm font-medium text-slate-700">{s.label}</span>
              <input
                type="text"
                data-testid={`tk-${s.date}`}
                value={sheet[s.date] || ''}
                onChange={(e) => setField(s.date, e.target.value)}
                placeholder="DD/MM/YYYY"
                className="col-span-7 px-3 py-1.5 border border-[#E2E2DF] rounded-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#7E22CE]/20 focus:border-[#7E22CE]"
              />
            </div>
          ))}

          <div className="flex items-center justify-end gap-2 pt-3 mt-2 border-t border-[#E2E2DF]">
            {saved && <span className="text-xs text-emerald-700 font-semibold">Saved ✓</span>}
            <button
              type="button"
              data-testid="tk-save-btn"
              onClick={save}
              disabled={saving}
              className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md bg-[#7E22CE] text-white hover:bg-[#6B21A8] disabled:opacity-50"
            >
              <Save size={13} />{saving ? 'Saving…' : 'Save'}
            </button>
            <button
              type="button"
              data-testid="tk-preview-btn"
              onClick={() => onOpenPdf?.('job-tracking-sheet')}
              className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border border-[#7E22CE] text-[#7E22CE] hover:bg-purple-50"
            >
              <FileText size={13} /> Preview PDF
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
